import { useCallback, useEffect, useState } from "react";
import { ChatCircleDots, Plus, Trash, UsersThree, WarningDiamond } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const KIND_META = {
  discuss: { label: "Discuss", cls: "bg-[hsl(210_70%_45%)] text-white" },
  highlight: { label: "Highlight", cls: "bg-[hsl(42_92%_48%)] text-navy" },
  concern: { label: "Concern", cls: "bg-[hsl(14_78%_52%)] text-white" },
  deviation: { label: "Deviation", cls: "bg-[hsl(340_62%_44%)] text-white" },
};
const STATUSES = ["open", "discussed", "closed"];

const Roster = ({ members, directory, canEdit, reload }) => {
  const { tenantId } = useAuth();
  const [form, setForm] = useState({ name: "", relation: "", role: "", user_id: "", is_kartha: false });

  const add = async () => {
    if (!form.name.trim()) return toast.error("Enter the family member's name");
    try {
      await api.post(`/tenants/${tenantId}/viveka/charter/members`, {
        ...form, user_id: form.user_id || null,
      });
      setForm({ name: "", relation: "", role: "", user_id: "", is_kartha: false });
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/tenants/${tenantId}/viveka/charter/members/${m.id}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="panel overflow-hidden" data-testid="charter-roster">
      <div className="flex items-center gap-2 border-b border-border bg-navy/5 px-5 py-3">
        <UsersThree size={17} className="text-navy" />
        <h3 className="font-display text-lg text-navy">Family members</h3>
      </div>
      <div className="divide-y divide-border">
        {members.length === 0 && (
          <p className="px-5 py-4 text-sm text-muted-foreground">No family members added yet.</p>
        )}
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 px-5 py-3"
               data-testid={`roster-member-${m.id}`}>
            <div>
              <p className="text-sm font-medium text-navy">
                {m.name}
                {m.is_kartha && (
                  <span className="ml-2 rounded-full bg-plum px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-white">
                    Kartha
                  </span>
                )}
              </p>
              <p className="label-caps mt-1">
                {[m.relation, m.role].filter(Boolean).join(" · ") || "—"}
                {m.user_id ? " · portal login linked" : ""}
              </p>
            </div>
            {canEdit && (
              <button onClick={() => remove(m)} data-testid={`roster-delete-${m.id}`}
                      className="rounded-full p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                <Trash size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="space-y-3 border-t border-border bg-muted/30 p-5">
          <p className="label-caps">Add a family member</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input placeholder="Name" value={form.name} data-testid="roster-name"
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum" />
            <input placeholder="Relation (e.g. Son)" value={form.relation} data-testid="roster-relation"
                   onChange={(e) => setForm({ ...form, relation: e.target.value })}
                   className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum" />
            <input placeholder="Role in business" value={form.role} data-testid="roster-role"
                   onChange={(e) => setForm({ ...form, role: e.target.value })}
                   className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum" />
            <select value={form.user_id} data-testid="roster-login"
                    onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                    className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum">
              <option value="">Link a portal login (optional)</option>
              {directory.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-navy">
            <input type="checkbox" checked={form.is_kartha} data-testid="roster-is-kartha"
                   onChange={(e) => setForm({ ...form, is_kartha: e.target.checked })} />
            This member is the Kartha (Chairman)
          </label>
          <button onClick={add} data-testid="roster-add"
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-navy/90">
            <Plus size={13} /> Add member
          </button>
        </div>
      )}
    </div>
  );
};

export const CharterDiscussion = ({ sections, canEdit }) => {
  const { tenantId, user, isPlatformAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({
    kind: "discuss", body: "", section_key: "", tagged_member_ids: [],
    observed_on: "", deviation_status: "open",
  });

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [m, n] = await Promise.all([
        api.get(`/tenants/${tenantId}/viveka/charter/members`),
        api.get(`/tenants/${tenantId}/viveka/charter/discussion`),
      ]);
      setMembers(m.data.members);
      setDirectory(m.data.directory);
      setNotes(n.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const toggleTag = (id) =>
    setForm((f) => ({
      ...f,
      tagged_member_ids: f.tagged_member_ids.includes(id)
        ? f.tagged_member_ids.filter((x) => x !== id)
        : [...f.tagged_member_ids, id],
    }));

  const post = async () => {
    if (!form.body.trim()) return toast.error("Please write your note");
    try {
      await api.post(`/tenants/${tenantId}/viveka/charter/discussion`, {
        ...form, section_key: form.section_key || null, observed_on: form.observed_on || null,
      });
      setForm({ kind: "discuss", body: "", section_key: "", tagged_member_ids: [], observed_on: "", deviation_status: "open" });
      toast.success("Note added to the charter discussion");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (note, status) => {
    try {
      await api.patch(`/tenants/${tenantId}/viveka/charter/discussion/${note.id}`,
                      { deviation_status: status });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (note) => {
    try {
      await api.delete(`/tenants/${tenantId}/viveka/charter/discussion/${note.id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const kartha = members.find((m) => m.is_kartha);
  const sectionTitle = (key) => sections.find((s) => s.key === key)?.title;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]" data-testid="charter-discussion">
      <div className="space-y-6">
        <div className="panel overflow-hidden">
          <div className="flex items-center gap-2 border-b border-border bg-plum/5 px-5 py-3">
            <ChatCircleDots size={17} className="text-plum" />
            <h3 className="font-display text-lg text-navy">Raise a point on the charter</h3>
          </div>
          <div className="space-y-4 p-5">
            <div className="flex flex-wrap gap-2">
              {Object.entries(KIND_META).map(([k, meta]) => (
                <button key={k} onClick={() => setForm({ ...form, kind: k })}
                        data-testid={`note-kind-${k}`}
                        className={`rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                          form.kind === k ? meta.cls : "border border-border text-muted-foreground hover:border-plum/40"}`}>
                  {meta.label}
                </button>
              ))}
            </div>
            <select value={form.section_key} data-testid="note-section"
                    onChange={(e) => setForm({ ...form, section_key: e.target.value })}
                    className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum">
              <option value="">Charter section (optional)</option>
              {sections.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
            </select>
            {form.kind === "deviation" && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="label-caps">Date observed</span>
                  <input type="date" value={form.observed_on} data-testid="note-observed-on"
                         onChange={(e) => setForm({ ...form, observed_on: e.target.value })}
                         className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum" />
                </label>
                <label className="block">
                  <span className="label-caps">Status</span>
                  <select value={form.deviation_status} data-testid="note-status"
                          onChange={(e) => setForm({ ...form, deviation_status: e.target.value })}
                          className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm capitalize outline-none focus:border-plum">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
            )}
            <textarea rows={4} value={form.body} data-testid="note-body"
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      placeholder="What would you like the family to discuss?"
                      className="w-full resize-y rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-plum" />
            <div>
              <p className="label-caps">Tag family members</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {members.length === 0 && (
                  <p className="text-sm text-muted-foreground">Add family members on the right to tag them.</p>
                )}
                {members.map((m) => (
                  <button key={m.id} onClick={() => toggleTag(m.id)} data-testid={`note-tag-${m.id}`}
                          className={`rounded-full px-4 py-2 text-xs transition-colors ${
                            form.tagged_member_ids.includes(m.id)
                              ? "bg-navy text-white" : "border border-border text-navy hover:border-navy/50"}`}>
                    {m.name}{m.is_kartha ? " (Kartha)" : ""}
                  </button>
                ))}
              </div>
              {kartha && !form.tagged_member_ids.includes(kartha.id) && (
                <button onClick={() => toggleTag(kartha.id)} data-testid="note-tag-kartha"
                        className="mt-3 text-[11px] uppercase tracking-[0.14em] text-plum hover:underline">
                  Tag the Kartha directly
                </button>
              )}
            </div>
            <button onClick={post} data-testid="note-submit"
                    className="rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
              Post to discussion
            </button>
          </div>
        </div>

        <div className="space-y-4" data-testid="charter-notes">
          {notes.length === 0 && (
            <p className="panel p-6 text-sm text-muted-foreground">No points raised yet.</p>
          )}
          {notes.map((n) => (
            <div key={n.id} className="panel p-5" data-testid={`charter-note-${n.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.14em] ${KIND_META[n.kind]?.cls}`}>
                  {KIND_META[n.kind]?.label || n.kind}
                </span>
                {n.section_key && (
                  <span className="rounded-full bg-muted px-3 py-1 text-[10px] text-navy">
                    {sectionTitle(n.section_key) || n.section_key}
                  </span>
                )}
                {n.kind === "deviation" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-plum">
                    <WarningDiamond size={12} />
                    {n.observed_on ? `Observed ${n.observed_on} · ` : ""}{n.deviation_status}
                  </span>
                )}
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  {(n.created_at || "").slice(0, 10)}
                </span>
              </div>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground">{n.body}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <p className="label-caps">{n.author_name}</p>
                {(n.tagged_names || []).length > 0 && (
                  <p className="text-[11px] text-plum">Tagged: {n.tagged_names.join(", ")}</p>
                )}
                {n.kind === "deviation" && canEdit && (
                  <select value={n.deviation_status || "open"} data-testid={`note-set-status-${n.id}`}
                          onChange={(e) => setStatus(n, e.target.value)}
                          className="rounded-full border border-border bg-card px-3 py-1 text-[11px] capitalize outline-none">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                {(isPlatformAdmin || n.author_id === user?.id) && (
                  <button onClick={() => remove(n)} data-testid={`note-delete-${n.id}`}
                          className="ml-auto rounded-full p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                    <Trash size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Roster members={members} directory={directory} canEdit={canEdit} reload={load} />
    </div>
  );
};
